import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useRegisterMutation, useUpdateMutation } from '../../app/services/locations';
import { fetchLocation, fetchLocations } from '../../features/locations/locationsSlice';
import { commonTextFieldStyles } from '../../utils/textFieldStyles';
import { iconButtonStyles } from '../../utils/iconButtonStyles';
import { MODAL_STYLES } from '../../utils/modalStyles';
import ModalComponent from '../ModalComponent';
import ConfirmDiscardDialog from '../ConfirmDiscardDialog';
import { useUnsavedChangesWarning } from '../../hooks/useUnsavedChangesWarning';
import { useTranslation } from 'react-i18next';

import {
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
  Tooltip,
} from '@mui/material';
import { ModalDialogActions, ModalButton, ModalCloseButton, ModalAddButton } from '../ModalComponents';
import EditIcon from '@mui/icons-material/Edit';

import styles from '../ModalComponent/modalStyles.module.css';
import { validateFormData, getValidationErrorMessage } from '../../helpers';
import { postLocationSchema } from '../../schemas';

const initialForm = {
  name: '',
  description: '',
};

export default function ModalAddLocation({ id, type }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [errorResponse, setErrorResponse] = useState('');
  const [isDirty, setIsDirty] = useState(false);
  const [registerLocations, { error }] = useRegisterMutation();

  const [formValidationErrors, setFormValidationErrors] = useState(null);
  const locationNameError = getValidationErrorMessage(formValidationErrors, 'name');
  const locationDescrError = getValidationErrorMessage(formValidationErrors, 'description');

  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [updateLocation] = useUpdateMutation();

  const { guardClose, confirmProps } = useUnsavedChangesWarning(isDirty, open);

  const handleOpen = () => {
    setOpen(true);
  };
  const doClose = () => {
    setOpen(false);
    setForm(initialForm);
    setFormValidationErrors(null);
    setIsDirty(false);
  };
  const handleClose = () => guardClose(doClose);

  useEffect(() => {
    if (id && open) {
      async function fetchData() {
        try {
          const response = await dispatch(fetchLocation(id));
          setForm({
            name: response.payload.name,
            description: response.payload.description,
          });
        } catch (error) {
          console.error('Error fetching location:', error);
        }
      }
      fetchData();
    }
  }, [id, open, dispatch]);

  useEffect(() => {
    if (error) {
      console.log(error);
      setErrorResponse(error.data.message);
    }
  }, [error]);

  const handleChange = (e) => {
    setErrorResponse('');
    setIsDirty(true);
    setForm({
      ...form,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async () => {
    const validationErrors = validateFormData(t, postLocationSchema, form);
    if (!!validationErrors) {
      setFormValidationErrors(validationErrors);
      return;
    }
    if (id) {
      try {
        await updateLocation({ id, form });
        doClose();
        dispatch(fetchLocations());
      } catch (err) {
        console.error('Error registering location:', err);
      }
    } else {
      try {
        await registerLocations(form);
        doClose();
        dispatch(fetchLocations());
      } catch (err) {
        console.error('Error registering location:', err);
      }
    }
  };

  return (
    <div>
      {type === 'add' ? (
        <ModalAddButton onClick={handleOpen} />
      ) : (
        <Tooltip title={t('generic.edit')} placement="top">
          <IconButton
            color="primary"
            onClick={handleOpen}
            style={iconButtonStyles}
          >
            <EditIcon />
          </IconButton>
        </Tooltip>
      )}
      <ModalComponent open={open} handleClose={handleClose}>
        <div className={styles.modal}>
          <DialogTitle sx={MODAL_STYLES.dialogTitle}>
            {type === 'add' ? t('modals.createLocation') : t('modals.updateLocation')}
          </DialogTitle>
          <ModalCloseButton onClick={handleClose} />
          <DialogContent dividers sx={MODAL_STYLES.dialogContent}>
            <div style={MODAL_STYLES.formContainer}>
              <TextField
                label={t('generic.name')}
                variant="outlined"
                value={form.name}
                name="name"
                onChange={handleChange}
                fullWidth
                error={!!locationNameError}
                helperText={locationNameError ?? ' '}
                sx={commonTextFieldStyles}
              />
              <TextField
                label={t('generic.description')}
                variant="outlined"
                value={form.description}
                name="description"
                onChange={handleChange}
                fullWidth
                error={!!locationDescrError}
                helperText={locationDescrError ?? ' '}
                sx={commonTextFieldStyles}
              />

              {errorResponse &&
                (Array.isArray(errorResponse) ? (
                  errorResponse.map((item, index) => (
                    <Typography key={index} color="error">
                      {item}
                    </Typography>
                  ))
                ) : (
                  <Typography color="error">{errorResponse}</Typography>
                ))}
            </div>
          </DialogContent>
          <ModalDialogActions>
            <ModalButton
              variant="outlined"
              color="primary"
              onClick={handleClose}
            >
              {t('modals.cancel')}
            </ModalButton>
            <ModalButton
              variant="contained"
              color="primary"
              onClick={handleSubmit}
            >
              {type === 'add' ? t('modals.create') : t('modals.update')}
            </ModalButton>
          </ModalDialogActions>
        </div>
      </ModalComponent>
      <ConfirmDiscardDialog {...confirmProps} />
    </div>
  );
}
